import { useEffect, useState } from "react";
import { authClient, signIn, signUp } from "@/lib/authClient";
import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, CheckCircle2, Gamepad2, Mail, Megaphone, RefreshCw, Wrench } from "lucide-react";
import { useLocation } from "wouter";

const platformRoles = [
  {
    key: "player" as const,
    icon: Gamepad2,
    label: "Player",
    desc: "Compete in tournaments & earn prizes.",
  },
  {
    key: "creator" as const,
    icon: Wrench,
    label: "Creator",
    desc: "Design & host skill-based games.",
  },
  {
    key: "promoter" as const,
    icon: Megaphone,
    label: "Promoter",
    desc: "Earn commission promoting games.",
  },
];

export default function Login() {
  const { isAuthenticated, loading } = useAuth();
  const [, setLocation] = useLocation();
  const [tab, setTab] = useState<"signin" | "signup">("signin");

  // Sign-in state
  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [signInError, setSignInError] = useState("");
  const [signInLoading, setSignInLoading] = useState(false);
  // When set, the user tried to sign in with an unverified email — show resend UI for this address
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendResult, setResendResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Sign-up state
  const [signUpName, setSignUpName] = useState("");
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [signUpRole, setSignUpRole] = useState<"player" | "creator" | "promoter">("player");
  const [signUpError, setSignUpError] = useState("");
  const [signUpAdult, setSignUpAdult] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);

  // Re-auth guard — redirect already-logged-in users to console
  useEffect(() => {
    if (!loading && isAuthenticated) {
      setLocation("/console");
    }
  }, [isAuthenticated, loading, setLocation]);

  if (!loading && isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <p className="text-muted-foreground">Already signed in — redirecting…</p>
      </div>
    );
  }

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignInError("");
    setUnverifiedEmail(null);
    setResendResult(null);
    setSignInLoading(true);
    try {
      const result = await signIn.email({
        email: signInEmail,
        password: signInPassword,
        callbackURL: "/",
      });

      if (result.error) {
        // Better Auth sets code="EMAIL_NOT_VERIFIED" for unverified accounts.
        // Fall back to a message substring match only — do NOT match on status 403,
        // which could be returned by unrelated auth restrictions (rate limit, geo-block).
        const code = (result.error as { code?: string }).code;
        const msg = result.error.message ?? "";
        const isUnverified =
          code === "EMAIL_NOT_VERIFIED" ||
          msg.toLowerCase().includes("email") && msg.toLowerCase().includes("verif");
        if (isUnverified) {
          setUnverifiedEmail(signInEmail);
        } else {
          setSignInError(msg || "Sign in failed");
        }
        return;
      }

      // Defensive: a "successful" signIn call should produce a session.
      // If it doesn't (e.g. cookie not set due to proxy misconfig), surface a clear error
      // instead of redirecting to a page that will just show Sign In again.
      const session = await authClient.getSession();
      if (!session?.data?.session) {
        setSignInError(
          "Sign in didn't complete — please try again. If this keeps happening, check that cookies are enabled.",
        );
        return;
      }

      window.location.replace("/");
    } catch (err: any) {
      const msg: string = err?.message ?? "Sign in failed";
      const lower = msg.toLowerCase();
      if (lower.includes("email") && lower.includes("verif")) {
        setUnverifiedEmail(signInEmail);
      } else {
        setSignInError(msg);
      }
    } finally {
      setSignInLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendingVerification(true);
    setResendResult(null);
    try {
      const result = await authClient.sendVerificationEmail({
        email: unverifiedEmail,
        callbackURL: "/profile-setup",
      });
      if (result.error) {
        setResendResult({
          ok: false,
          message: result.error.message ?? "Failed to send. Please try again.",
        });
      } else {
        setResendResult({
          ok: true,
          message: `Verification email sent to ${unverifiedEmail}. Check your inbox (and spam folder).`,
        });
      }
    } catch (err: any) {
      setResendResult({
        ok: false,
        message: err?.message ?? "Failed to send. Please try again.",
      });
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignUpError("");
    if (!signUpAdult) {
      setSignUpError("You must be 18 or older to create an account.");
      return;
    }
    setSignUpLoading(true);
    try {
      // Stash chosen role so ProfileSetup can pre-select it after verification
      sessionStorage.setItem("gp.signupRole", signUpRole);
      sessionStorage.setItem("gp.signupEmail", signUpEmail);

      const result = await signUp.email({
        email: signUpEmail,
        password: signUpPassword,
        name: signUpName,
        callbackURL: "/profile-setup",
      });
      if (result.error) {
        setSignUpError(result.error.message ?? "Registration failed");
      } else {
        // Redirect to confirmation screen (email verification pending)
        setLocation(`/signup-success?email=${encodeURIComponent(signUpEmail)}`);
      }
    } catch (err: any) {
      setSignUpError(err?.message ?? "Registration failed");
    } finally {
      setSignUpLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight">GamePoint</h1>
          <p className="text-muted-foreground mt-2">AI-powered game creation platform</p>
        </div>

        <Tabs
          value={tab}
          onValueChange={(v) => {
            setTab(v as "signin" | "signup");
            // Clear unverified-email callout when switching tabs so stale state doesn't persist
            setUnverifiedEmail(null);
            setResendResult(null);
            setSignInError("");
          }}
        >
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="signin">Sign In</TabsTrigger>
            <TabsTrigger value="signup">Create Account</TabsTrigger>
          </TabsList>

          {/* ── Sign In ── */}
          <TabsContent value="signin">
            <Card>
              <CardHeader>
                <CardTitle>Sign In</CardTitle>
                <CardDescription>Enter your email and password to continue</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Unverified-email callout */}
                {unverifiedEmail && (
                  <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <p className="font-semibold text-amber-200">
                          Your email isn't verified yet
                        </p>
                        <p className="text-amber-100/80 mt-1">
                          We sent a confirmation link to <span className="font-semibold break-all">{unverifiedEmail}</span>.
                          Click it to activate your account, or resend below.
                        </p>
                      </div>
                    </div>

                    {resendResult ? (
                      <div className={`flex items-start gap-2 text-xs ${resendResult.ok ? "text-neon" : "text-destructive"}`}>
                        {resendResult.ok ? (
                          <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        ) : (
                          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        )}
                        <span>{resendResult.message}</span>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={handleResendVerification}
                          disabled={resendingVerification}
                          className="gap-1.5 border-amber-500/40 text-amber-100 hover:bg-amber-500/20"
                        >
                          {resendingVerification ? (
                            <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                          ) : (
                            <><RefreshCw className="w-3.5 h-3.5" /> Resend verification email</>
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setUnverifiedEmail(null);
                            setResendResult(null);
                          }}
                          className="text-amber-100/70 hover:text-amber-100"
                        >
                          Use a different account
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signInEmail}
                      onChange={(e) => setSignInEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      placeholder="••••••••"
                      value={signInPassword}
                      onChange={(e) => setSignInPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                  </div>
                  {signInError && (
                    <p className="text-sm text-destructive">{signInError}</p>
                  )}
                  <Button type="submit" className="w-full" disabled={signInLoading}>
                    {signInLoading ? "Signing in…" : "Sign In"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Create Account ── */}
          <TabsContent value="signup">
            <Card>
              <CardHeader>
                <CardTitle>Create Account</CardTitle>
                <CardDescription>Sign up to start creating games</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Your name"
                      value={signUpName}
                      onChange={(e) => setSignUpName(e.target.value)}
                      required
                      autoComplete="name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signUpEmail}
                      onChange={(e) => setSignUpEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={signUpPassword}
                      onChange={(e) => setSignUpPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                    <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
                  </div>

                  {/* Role selector */}
                  <div className="space-y-2">
                    <Label>I want to…</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {platformRoles.map((role) => {
                        const Icon = role.icon;
                        const selected = signUpRole === role.key;
                        return (
                          <button
                            key={role.key}
                            type="button"
                            onClick={() => setSignUpRole(role.key)}
                            className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border text-center transition-all ${
                              selected
                                ? "border-neon bg-neon/10 text-neon"
                                : "border-border bg-background text-muted-foreground hover:border-neon/40"
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                            <span className="text-xs font-bold">{role.label}</span>
                            <span className="text-[10px] leading-tight">{role.desc}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Age gate: GamePoint events are for players 18+ */}
                  <label htmlFor="signup-adult" className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                    <input
                      id="signup-adult"
                      type="checkbox"
                      checked={signUpAdult}
                      onChange={(e) => setSignUpAdult(e.target.checked)}
                      required
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-neon,#00fa80)]"
                    />
                    <span>
                      I'm 18 or older and I agree to the{" "}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">Terms</a>{" "}
                      and the{" "}
                      <a href="/acceptable-use" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">Acceptable Use Policy</a>.
                    </span>
                  </label>

                  {signUpError && (
                    <p className="text-sm text-destructive">{signUpError}</p>
                  )}
                  <Button type="submit" className="w-full" disabled={signUpLoading || !signUpAdult}>
                    {signUpLoading ? "Creating account…" : "Create Account"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
